import React, { useState } from 'react';
import { X, Mail, Globe, AlertCircle, CheckCircle2, Sparkles } from 'lucide-react';

interface ApplicationFormProps {
  isOpen: boolean;
  onClose: () => void;
  language: 'zh' | 'en' | 'de' | 'fr' | 'ja' | 'ko' | 'ar';
}

export default function ApplicationForm({ isOpen, onClose, language }: ApplicationFormProps) {
  const [formData, setFormData] = useState({
    toolName: '',
    toolDescription: '',
    toolUrl: '',
    category: '',
    icon: '',
    email: '',
    applicantName: '',
    isAINative: false,
    techStack: '',
    aiNativeReason: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const t = {
    title: language === 'zh' ? '申请入驻 Douya Center' :
           language === 'en' ? 'Apply to Join Douya Center' :
           language === 'de' ? 'Beantragen Sie die Aufnahme bei Douya Center' :
           language === 'fr' ? 'Postuler pour rejoindre Douya Center' :
           language === 'ja' ? 'Douya Centerに参加申請' :
           language === 'ko' ? 'Douya Center 입점 신청' :
           'التقديم للانضمام إلى Douya Center',

    subtitle: language === 'zh' ? '提交您的AI原生应用，让更多人发现' :
              language === 'en' ? 'Submit your AI-native app and get discovered' :
              language === 'de' ? 'Reichen Sie Ihre AI-native App ein' :
              language === 'fr' ? 'Soumettez votre application AI-native' :
              language === 'ja' ? 'AIネイティブアプリを提出' :
              language === 'ko' ? 'AI 네이티브 앱 제출' :
              'قدم طلبك للتطبيق AI الأصلي',

    notice: language === 'zh' ? '⚠️ 仅接受2026年1月1日后发布的AI原生应用申请。审核通过后将通过邮件通知。' :
             language === 'en' ? '⚠️ Only AI-native apps released after Jan 1, 2026 are accepted. Approval will be notified via email.' :
             language === 'de' ? '⚠️ Nur AI-native Apps, die nach dem 1. Jan. 2026 veröffentlicht wurden, werden akzeptiert.' :
             language === 'fr' ? '⚠️ Seules les applications AI-native publiées après le 1er janv. 2026 sont acceptées.' :
             language === 'ja' ? '⚠️ 2026年1月1日以降にリリースされたAIネイティブアプリのみ受付。' :
             language === 'ko' ? '⚠️ 2026년 1월 1일 이후에 출시된 AI 네이티브 앱만 접수 가능.' :
             '⚠️ يتم قبول تطبيقات AI الأصلية التي تم إصدارها بعد 1 يناير 2026 فقط.',

    toolName: language === 'zh' ? '工具名称' :
              language === 'en' ? 'Tool Name' :
              language === 'de' ? 'Tool-Name' :
              language === 'fr' ? 'Nom de l\'outil' :
              language === 'ja' ? 'ツール名' :
              language === 'ko' ? '도구 이름' :
              'اسم الأداة',

    toolDescription: language === 'zh' ? '工具描述（最多200字）' :
                     language === 'en' ? 'Description (max 200 chars)' :
                     language === 'de' ? 'Beschreibung (max. 200 Zeichen)' :
                     language === 'fr' ? 'Description (max 200 caractères)' :
                     language === 'ja' ? '説明（最大200文字）' :
                     language === 'ko' ? '설명（최대 200자）' :
                     'الوصف (حد أقصى 200 حرف)',

    toolUrl: language === 'zh' ? '工具网址' :
             language === 'en' ? 'Tool URL' :
             language === 'de' ? 'Tool-URL' :
             language === 'fr' ? 'URL de l\'outil' :
             language === 'ja' ? 'ツールURL' :
             language === 'ko' ? '도구 URL' :
             'رابط الأداة',

    category: language === 'zh' ? '分类' :
              language === 'en' ? 'Category' :
              language === 'de' ? 'Kategorie' :
              language === 'fr' ? 'Catégorie' :
              language === 'ja' ? 'カテゴリ' :
              language === 'ko' ? '카테고리' :
              'الفئة',

    icon: language === 'zh' ? '图标（Emoji）' :
           language === 'en' ? 'Icon (Emoji)' :
           language === 'de' ? 'Symbol (Emoji)' :
           language === 'fr' ? 'Icône (Emoji)' :
           language === 'ja' ? 'アイコン（絵文字）' :
           language === 'ko' ? '아이콘（이모지）' :
           'الأيقونة (رمز تعبيري)',

    email: language === 'zh' ? '联系邮箱' :
            language === 'en' ? 'Contact Email' :
            language === 'de' ? 'Kontakt-E-Mail' :
            language === 'fr' ? 'E-mail de contact' :
            language === 'ja' ? '連絡先メール' :
            language === 'ko' ? '연락처 이메일' :
            'البريد الإلكتروني للاتصال',

    applicantName: language === 'zh' ? '申请人姓名' :
                   language === 'en' ? 'Applicant Name' :
                   language === 'de' ? 'Name des Antragstellers' :
                   language === 'fr' ? 'Nom du demandeur' :
                   language === 'ja' ? '申請者名' :
                   language === 'ko' ? '신청자 이름' :
                   'اسم مقدم الطلب',

    isAINative: language === 'zh' ? '我确认这是2026年1月1日后发布的AI原生应用' :
                 language === 'en' ? 'I confirm this is an AI-native app released after Jan 1, 2026' :
                 language === 'de' ? 'Ich bestätige, dass dies eine AI-native App nach dem 1. Jan. 2026 ist' :
                 language === 'fr' ? 'Je confirme qu\'il s\'agit d\'une application AI-native publiée après le 1er janv. 2026' :
                 language === 'ja' ? 'これは2026年1月1日以降にリリースされたAIネイティブアプリであることを確認' :
                 language === 'ko' ? '2026년 1월 1일 이후에 출시된 AI 네이티브 앱임을 확인' :
                 'أؤكد أن هذا تطبيق AI أصلي تم إصداره بعد 1 يناير 2026',

    techStack: language === 'zh' ? '技术栈' :
               language === 'en' ? 'Tech Stack' :
               language === 'de' ? 'Tech-Stack' :
               language === 'fr' ? 'Stack technologique' :
               language === 'ja' ? '技術スタック' :
               language === 'ko' ? '기술 스택' :
               'المجموعة التقنية',

    aiNativeReason: language === 'zh' ? '为何是AI原生应用？' :
                    language === 'en' ? 'Why is this AI-native?' :
                    language === 'de' ? 'Warum ist das AI-native?' :
                    language === 'fr' ? 'Pourquoi est-ce AI-native?' :
                    language === 'ja' ? 'なぜAIネイティブなのか？' :
                    language === 'ko' ? '왜 AI 네이티브인가?' :
                    'لماذا هذا AI أصلي؟',

    submit: language === 'zh' ? '提交申请' :
             language === 'en' ? 'Submit Application' :
             language === 'de' ? 'Antrag absenden' :
             language === 'fr' ? 'Soumettre la demande' :
             language === 'ja' ? '申請を送信' :
             language === 'ko' ? '신청 제출' :
             'تقديم الطلب',

    cancel: language === 'zh' ? '取消' :
             language === 'en' ? 'Cancel' :
             language === 'de' ? 'Abbrechen' :
             language === 'fr' ? 'Annuler' :
             language === 'ja' ? 'キャンセル' :
             language === 'ko' ? '취소' :
             'إلغاء',

    success: language === 'zh' ? '✓ 申请提交成功！我们将在审核后通过邮件通知您。' :
              language === 'en' ? '✓ Application submitted! We will notify you via email after review.' :
              language === 'de' ? '✓ Antrag eingereicht! Wir benachrichtigen Sie per E-Mail nach Prüfung.' :
              language === 'fr' ? '✓ Demande soumise! Nous vous informeronpar e-mail après examen.' :
              language === 'ja' ? '✓ 申請を送信しました！審査後メールでお知らせします。' :
              language === 'ko' ? '✓ 신청이 제출되었습니다! 심사 후 이메일로 알려드리겠습니다.' :
              '✓ تم تقديم الطلب! سنخطرك عبر البريد الإلكتروني بعد المراجعة.',
  };

  const categories = language === 'zh' ? [
    '效率工具', '内容创作', '设计工具', '研究分析', '职业发展',
    '教育学习', '生活服务', '营销推广', '创意工具', '社区', '开发工具'
  ] : [
    'Productivity', 'Content', 'Design', 'Research', 'Career',
    'Education', 'Lifestyle', 'Marketing', 'Creative', 'Community', 'Dev Tools'
  ];

  // 安全验证函数
  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validateUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const sanitizeInput = (input: string) => {
    return input.trim().replace(/<[^>]*>/g, '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: Record<string, string> = {};

    // 验证工具名称
    if (!formData.toolName || formData.toolName.length < 2) {
      newErrors.toolName = language === 'zh' ? '请输入有效的工具名称' : 'Please enter a valid tool name';
    }

    // 验证描述
    if (!formData.toolDescription || formData.toolDescription.length < 10) {
      newErrors.toolDescription = language === 'zh' ? '描述至少需要10个字符' : 'Description must be at least 10 characters';
    }
    if (formData.toolDescription.length > 200) {
      newErrors.toolDescription = language === 'zh' ? '描述不能超过200个字符' : 'Description cannot exceed 200 characters';
    }

    // 验证URL
    if (!formData.toolUrl || !validateUrl(formData.toolUrl)) {
      newErrors.toolUrl = language === 'zh' ? '请输入有效的URL' : 'Please enter a valid URL';
    }

    // 验证邮箱
    if (!formData.email || !validateEmail(formData.email)) {
      newErrors.email = language === 'zh' ? '请输入有效的邮箱地址' : 'Please enter a valid email address';
    }

    // 验证申请人
    if (!formData.applicantName || formData.applicantName.length < 2) {
      newErrors.applicantName = language === 'zh' ? '请输入您的姓名' : 'Please enter your name';
    }

    // 验证AI原生确认
    if (!formData.isAINative) {
      newErrors.isAINative = language === 'zh' ? '请确认这是AI原生应用' : 'Please confirm this is an AI-native app';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    // 模拟提交（实际需要连接后端API）
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    // 3秒后关闭
    setTimeout(() => {
      onClose();
      setSubmitSuccess(false);
      setFormData({
        toolName: '',
        toolDescription: '',
        toolUrl: '',
        category: '',
        icon: '',
        email: '',
        applicantName: '',
        isAINative: false,
        techStack: '',
        aiNativeReason: ''
      });
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">{t.title}</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Notice */}
          <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 dark:text-amber-200">{t.notice}</p>
            </div>
          </div>

          {submitSuccess ? (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-lg font-medium text-gray-800 dark:text-gray-100">{t.success}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tool Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.toolName} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.toolName}
                  onChange={(e) => setFormData({ ...formData, toolName: sanitizeInput(e.target.value) })}
                  maxLength={50}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.toolName ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                  placeholder={language === 'zh' ? '例如：AI Writer Pro' : 'e.g., AI Writer Pro'}
                />
                {errors.toolName && <p className="text-xs text-red-500 mt-1">{errors.toolName}</p>}
              </div>

              {/* Tool Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.toolDescription} <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.toolDescription}
                  onChange={(e) => setFormData({ ...formData, toolDescription: sanitizeInput(e.target.value) })}
                  maxLength={200}
                  rows={3}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.toolDescription ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none`}
                  placeholder={language === 'zh' ? '简要描述您的工具功能和特点' : 'Briefly describe your tool\'s features'}
                />
                <div className="flex justify-between mt-1">
                  {errors.toolDescription ? (
                    <p className="text-xs text-red-500">{errors.toolDescription}</p>
                  ) : (
                    <p></p>
                  )}
                  <p className="text-xs text-gray-400">{formData.toolDescription.length}/200</p>
                </div>
              </div>

              {/* Tool URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.toolUrl} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="url"
                    value={formData.toolUrl}
                    onChange={(e) => setFormData({ ...formData, toolUrl: e.target.value.trim() })}
                    className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.toolUrl ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="https://"
                  />
                </div>
                {errors.toolUrl && <p className="text-xs text-red-500 mt-1">{errors.toolUrl}</p>}
              </div>

              {/* Category & Icon */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.category} <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  >
                    <option value="">{language === 'zh' ? '选择分类' : 'Select Category'}</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t.icon}
                  </label>
                  <input
                    type="text"
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    maxLength={2}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-center text-2xl"
                    placeholder="🔍"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.email} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value.trim() })}
                    className={`w-full pl-10 pr-4 py-2 rounded-lg border ${errors.email ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                    placeholder="your@email.com"
                  />
                </div>
                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
              </div>

              {/* Applicant Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.applicantName} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.applicantName}
                  onChange={(e) => setFormData({ ...formData, applicantName: sanitizeInput(e.target.value) })}
                  maxLength={50}
                  className={`w-full px-4 py-2 rounded-lg border ${errors.applicantName ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'} bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`}
                  placeholder={language === 'zh' ? '您的姓名或昵称' : 'Your name or nickname'}
                />
                {errors.applicantName && <p className="text-xs text-red-500 mt-1">{errors.applicantName}</p>}
              </div>

              {/* Tech Stack */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.techStack}
                </label>
                <input
                  type="text"
                  value={formData.techStack}
                  onChange={(e) => setFormData({ ...formData, techStack: sanitizeInput(e.target.value) })}
                  maxLength={100}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder={language === 'zh' ? '例如：React, Python, TensorFlow' : 'e.g., React, Python, TensorFlow'}
                />
              </div>

              {/* AI Native Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t.aiNativeReason}
                </label>
                <textarea
                  value={formData.aiNativeReason}
                  onChange={(e) => setFormData({ ...formData, aiNativeReason: sanitizeInput(e.target.value) })}
                  maxLength={300}
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                  placeholder={language === 'zh' ? '请说明您的工具如何利用AI能力' : 'Explain how your tool leverages AI capabilities'}
                />
                <p className="text-xs text-gray-400 mt-1">{formData.aiNativeReason.length}/300</p>
              </div>

              {/* AI Native Confirmation */}
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isAINative}
                    onChange={(e) => setFormData({ ...formData, isAINative: e.target.checked })}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <span className={`text-sm ${errors.isAINative ? 'text-red-600 dark:text-red-400' : 'text-blue-800 dark:text-blue-200'}`}>
                    {t.isAINative}
                  </span>
                </label>
                {errors.isAINative && <p className="text-xs text-red-500 mt-2 ml-7">{errors.isAINative}</p>}
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium"
                >
                  {t.cancel}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {language === 'zh' ? '提交中...' : 'Submitting...'}
                    </>
                  ) : (
                    t.submit
                  )}
                </button>
              </div>

              {/* Security Note */}
              <p className="text-xs text-gray-400 text-center mt-4">
                🔒 {language === 'zh' ? '您的信息将被安全加密处理' : 'Your information will be securely encrypted'}
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
